import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Calendar,
  DollarSign,
  Eye,
  FileText,
  LayoutDashboard,
  RefreshCw,
  Search,
  User,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function InvoiceHistory() {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterDate, setFilterDate] = useState("");
  const [invoiceFrom, setInvoiceFrom] = useState("");
  const [invoiceTo, setInvoiceTo] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [summary, setSummary] = useState({
    totalInvoices: 0,
    totalRevenue: 0,
    uniqueClients: 0,
  });

  useEffect(() => {
    const token = localStorage.getItem("admin_token");

    if (!token) {
      navigate("/admin");
      return;
    }

    fetchInvoices();
  }, [navigate]);

  useEffect(() => {
    const token = localStorage.getItem("admin_token");

    if (!token) {
      return;
    }

    setPage(1);
  }, [searchTerm, filterDate, invoiceFrom, invoiceTo]);

  useEffect(() => {
    const token = localStorage.getItem("admin_token");

    if (!token) {
      return;
    }

    const timeoutId = setTimeout(() => {
      fetchInvoices();
    }, 250);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, filterDate, invoiceFrom, invoiceTo, page, pageSize]);

  const fetchInvoices = async () => {
    const token = localStorage.getItem("admin_token");
    const params = new URLSearchParams({
      skip: String((page - 1) * pageSize),
      limit: String(pageSize),
    });

    if (searchTerm.trim()) {
      params.set("search", searchTerm.trim());
    }
    if (filterDate) {
      params.set("date", filterDate);
    }
    if (invoiceFrom.trim()) {
      params.set("invoiceFrom", invoiceFrom.trim());
    }
    if (invoiceTo.trim()) {
      params.set("invoiceTo", invoiceTo.trim());
    }

    try {
      const response = await fetch(`${API}/admin/invoices?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setInvoices(data.invoices || []);
        setSummary(data.summary || {
          totalInvoices: data.count || (data.invoices || []).length,
          totalRevenue: 0,
          uniqueClients: 0,
        });
      } else if (response.status === 401) {
        localStorage.removeItem("admin_token");
        navigate("/admin");
      } else {
        toast.error("Failed to load invoices");
      }
    } catch (error) {
      console.error("Error fetching invoices:", error);
      toast.error("Error loading invoices");
    } finally {
      setLoading(false);
    }
  };

  const deleteInvoice = async (invoiceId, invoiceNumber) => {
    if (!window.confirm(`Delete invoice ${invoiceNumber}? This cannot be undone.`)) {
      return;
    }

    const token = localStorage.getItem("admin_token");

    try {
      const response = await fetch(`${API}/admin/invoices/${invoiceId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        toast.success(`Invoice ${invoiceNumber} deleted`);
        fetchInvoices();
        if (selectedInvoice?.id === invoiceId) {
          setSelectedInvoice(null);
          setShowModal(false);
        }
      } else {
        toast.error("Failed to delete invoice");
      }
    } catch (error) {
      console.error("Error deleting invoice:", error);
      toast.error("Error deleting invoice");
    }
  };

  const viewInvoiceDetails = (invoice) => {
    setSelectedInvoice(invoice);
    setShowModal(true);
  };

  const filteredInvoices = invoices;
  const totalInvoicesDisplay = summary.totalInvoices;
  const totalRevenueDisplay = summary.totalRevenue;
  const uniqueClientsDisplay = summary.uniqueClients;
  const totalPages = Math.max(1, Math.ceil(summary.totalInvoices / pageSize));
  const firstVisibleInvoice = summary.totalInvoices === 0 ? 0 : (page - 1) * pageSize + 1;
  const lastVisibleInvoice = Math.min(page * pageSize, summary.totalInvoices);

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[linear-gradient(180deg,#f7fbff_0%,#eef6ff_22%,#ffffff_50%,#f6fbff_100%)] dark:bg-[linear-gradient(180deg,#08111f_0%,#0b1729_35%,#0a1220_100%)]">
        <div className="text-gray-600 dark:text-gray-400">Loading invoices...</div>
      </div>
    );
  }

  const cardShell =
    "rounded-[1.75rem] border border-slate-200 bg-[linear-gradient(180deg,rgba(255,255,255,0.98),rgba(241,245,249,0.95))] p-6 shadow-[0_24px_65px_-28px_rgba(14,165,233,0.18)] dark:border-white/10 dark:bg-[linear-gradient(180deg,rgba(15,23,42,0.88),rgba(15,23,42,0.58))]";

  return (
    <div className="min-h-screen overflow-hidden bg-[linear-gradient(180deg,#f7fbff_0%,#eef6ff_22%,#ffffff_50%,#f6fbff_100%)] pt-16 text-slate-900 dark:bg-[linear-gradient(180deg,#08111f_0%,#0b1729_35%,#0a1220_100%)] dark:text-white">
      <section className="relative isolate overflow-hidden border-b border-white/50 dark:border-white/10">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute left-[-8rem] top-24 h-72 w-72 rounded-full bg-cyan-300/30 blur-3xl dark:bg-cyan-400/10" />
          <div className="absolute right-[-6rem] top-12 h-80 w-80 rounded-full bg-blue-500/20 blur-3xl dark:bg-blue-500/20" />
          <div className="absolute bottom-[-5rem] left-1/3 h-64 w-64 rounded-full bg-emerald-300/20 blur-3xl dark:bg-emerald-400/10" />
        </div>

        <div className="relative mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-3xl">
              <div className="mb-6 inline-flex items-center rounded-full border border-sky-200 bg-white/80 px-4 py-2 text-sm font-medium text-sky-700 shadow-sm backdrop-blur dark:border-sky-400/20 dark:bg-white/5 dark:text-sky-200">
                Admin invoice archive
              </div>
              <h1 className="text-4xl font-black tracking-tight text-slate-950 sm:text-5xl dark:text-white">
                Review, track, and manage
                <span className="block bg-gradient-to-r from-sky-500 via-cyan-400 to-emerald-400 bg-clip-text text-transparent">
                  every generated invoice.
                </span>
              </h1>
              <p className="mt-6 max-w-2xl text-lg leading-8 text-slate-600 dark:text-slate-300">
                Search invoice numbers, filter by date, inspect details, and keep a clean record of your invoice activity.
              </p>
            </div>

            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => navigate("/admin/dashboard")}
                className="inline-flex items-center gap-2 rounded-2xl bg-slate-900 px-5 py-3 font-semibold text-white transition duration-300 hover:bg-slate-800 dark:bg-white dark:text-slate-950 dark:hover:bg-slate-100"
              >
                <LayoutDashboard className="h-4 w-4" />
                Dashboard
              </button>
              <button
                onClick={() => fetchInvoices()}
                className="inline-flex items-center gap-2 rounded-2xl bg-slate-900 px-5 py-3 font-semibold text-white transition duration-300 hover:bg-slate-800 dark:bg-white dark:text-slate-950 dark:hover:bg-slate-100"
              >
                <RefreshCw className="h-4 w-4" />
                Refresh
              </button>
            </div>
          </div>

          <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-3">
            {[
              { icon: FileText, label: "Total invoices", value: totalInvoicesDisplay, iconColor: "from-sky-500 to-cyan-400" },
              { icon: DollarSign, label: "Total revenue", value: `$${totalRevenueDisplay.toFixed(2)}`, iconColor: "from-emerald-500 to-teal-400" },
              { icon: User, label: "Unique clients", value: uniqueClientsDisplay, iconColor: "from-violet-500 to-fuchsia-400" },
            ].map((item) => (
              <div key={item.label} className="rounded-2xl border border-white/70 bg-white/85 p-5 shadow-[0_20px_50px_-30px_rgba(15,23,42,0.4)] backdrop-blur dark:border-white/10 dark:bg-white/5">
                <div className="flex items-center gap-4">
                  <div className={`rounded-2xl bg-gradient-to-br ${item.iconColor} p-3 text-white`}>
                    <item.icon className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="text-sm text-slate-600 dark:text-slate-300">{item.label}</div>
                    <div className="text-2xl font-black text-slate-950 dark:text-white">{item.value}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className={`${cardShell} mb-6`}>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-[minmax(0,1.5fr)_minmax(10rem,0.75fr)_minmax(9rem,0.65fr)_minmax(9rem,0.65fr)_auto]">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search by invoice number or client name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full rounded-2xl border border-slate-200 bg-white py-3 pl-10 pr-4 text-slate-900 outline-none transition focus:border-sky-400 focus:ring-2 focus:ring-sky-200 dark:border-white/10 dark:bg-white dark:text-slate-900"
                />
              </div>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input
                  type="date"
                  value={filterDate}
                  onChange={(e) => setFilterDate(e.target.value)}
                  className="w-full rounded-2xl border border-slate-200 bg-white py-3 pl-10 pr-4 text-slate-900 outline-none transition focus:border-sky-400 focus:ring-2 focus:ring-sky-200 dark:border-white/10 dark:bg-white dark:text-slate-900"
                />
              </div>
              <div className="relative">
                <FileText className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder="From CIST-0026"
                  value={invoiceFrom}
                  onChange={(e) => setInvoiceFrom(e.target.value)}
                  className="w-full rounded-2xl border border-slate-200 bg-white py-3 pl-10 pr-4 text-slate-900 outline-none transition focus:border-sky-400 focus:ring-2 focus:ring-sky-200 dark:border-white/10 dark:bg-white dark:text-slate-900"
                />
              </div>
              <div className="relative">
                <FileText className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder="To latest"
                  value={invoiceTo}
                  onChange={(e) => setInvoiceTo(e.target.value)}
                  className="w-full rounded-2xl border border-slate-200 bg-white py-3 pl-10 pr-4 text-slate-900 outline-none transition focus:border-sky-400 focus:ring-2 focus:ring-sky-200 dark:border-white/10 dark:bg-white dark:text-slate-900"
                />
              </div>
              {(searchTerm || filterDate || invoiceFrom || invoiceTo) && (
                <button
                  onClick={() => {
                    setSearchTerm("");
                    setFilterDate("");
                    setInvoiceFrom("");
                    setInvoiceTo("");
                    setPage(1);
                  }}
                  className="rounded-2xl bg-slate-500 px-4 py-3 text-white transition hover:bg-slate-600"
                >
                  Clear Filters
                </button>
              )}
            </div>
          </div>

          <div className={`${cardShell} overflow-hidden p-0`}>
            <div className="flex flex-col gap-3 border-b border-slate-200 px-5 py-4 dark:border-white/10 md:flex-row md:items-center md:justify-between">
              <div className="text-sm text-slate-600 dark:text-slate-300">
                Showing {firstVisibleInvoice}-{lastVisibleInvoice} of {summary.totalInvoices} invoices
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setPage(1);
                  }}
                  className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-sky-400 focus:ring-2 focus:ring-sky-200 dark:border-white/10 dark:bg-white dark:text-slate-900"
                >
                  {[25, 50, 100, 200, 500].map((size) => (
                    <option key={size} value={size}>
                      {size} / page
                    </option>
                  ))}
                </select>
                <button
                  onClick={() => setPage(1)}
                  disabled={page === 1}
                  className="rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-40 dark:border-white/10 dark:text-slate-200 dark:hover:bg-white/10"
                >
                  First
                </button>
                <button
                  onClick={() => setPage((current) => Math.max(1, current - 1))}
                  disabled={page === 1}
                  className="rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-40 dark:border-white/10 dark:text-slate-200 dark:hover:bg-white/10"
                >
                  Previous
                </button>
                <span className="px-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
                  Page {page} of {totalPages}
                </span>
                <button
                  onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
                  disabled={page >= totalPages}
                  className="rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-40 dark:border-white/10 dark:text-slate-200 dark:hover:bg-white/10"
                >
                  Next
                </button>
                <button
                  onClick={() => setPage(totalPages)}
                  disabled={page >= totalPages}
                  className="rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-40 dark:border-white/10 dark:text-slate-200 dark:hover:bg-white/10"
                >
                  Last
                </button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-100/80 dark:bg-white/5">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-300">
                      Invoice #
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-300">
                      Client
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-300">
                      Date
                    </th>
                    <th className="px-6 py-4 text-right text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-300">
                      Total
                    </th>
                    <th className="px-6 py-4 text-center text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-300">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 dark:divide-white/10">
                  {filteredInvoices.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="px-6 py-16 text-center text-slate-500 dark:text-slate-400">
                        No invoices found
                      </td>
                    </tr>
                  ) : (
                    filteredInvoices.map((invoice) => (
                      <tr key={invoice.id} className="transition hover:bg-slate-50/80 dark:hover:bg-white/5">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="font-mono font-semibold text-slate-900 dark:text-white">
                            {invoice.invoiceNumber}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-slate-900 dark:text-white">{invoice.to_client?.name}</div>
                          <div className="text-sm text-slate-500 dark:text-slate-400">{invoice.to_client?.email}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-slate-600 dark:text-slate-300">
                          {invoice.date}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <span className="font-semibold text-slate-900 dark:text-white">
                            ${invoice.total?.toFixed(2)}
                          </span>
                          <span className="ml-1 text-sm text-slate-500 dark:text-slate-400">{invoice.currency}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <div className="flex justify-center gap-2">
                            <button
                              onClick={() => viewInvoiceDetails(invoice)}
                              className="rounded-xl p-2 text-sky-600 transition hover:bg-sky-100 dark:hover:bg-sky-900/20"
                              title="View Details"
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => deleteInvoice(invoice.id, invoice.invoiceNumber)}
                              className="rounded-xl p-2 text-red-600 transition hover:bg-red-100 dark:hover:bg-red-900/20"
                              title="Delete"
                            >
                              <FileText className="hidden" />
                              <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M3 6h18" />
                                <path d="M8 6V4h8v2" />
                                <path d="M19 6l-1 14H6L5 6" />
                                <path d="M10 11v6" />
                                <path d="M14 11v6" />
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {showModal && selectedInvoice && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/55 p-4 backdrop-blur-sm">
          <div className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-[2rem] border border-white/10 bg-white shadow-2xl dark:bg-[linear-gradient(180deg,rgba(15,23,42,0.96),rgba(15,23,42,0.86))]">
            <div className="sticky top-0 flex items-center justify-between border-b border-slate-200 bg-white/95 p-5 backdrop-blur dark:border-white/10 dark:bg-slate-950/85">
              <h2 className="text-xl font-bold text-slate-900 dark:text-white">
                Invoice Details: {selectedInvoice.invoiceNumber}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="rounded-xl px-3 py-2 text-slate-500 transition hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-white/10 dark:hover:text-white"
              >
                Close
              </button>
            </div>

            <div className="space-y-6 p-6">
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5 dark:border-white/10 dark:bg-white/5">
                  <h3 className="font-semibold text-slate-900 dark:text-white">From</h3>
                  <p className="mt-2 text-slate-700 dark:text-slate-300">{selectedInvoice.from_company?.name}</p>
                  <p className="whitespace-pre-line text-sm text-slate-500 dark:text-slate-400">
                    {selectedInvoice.from_company?.address}
                  </p>
                  <p className="text-sm text-slate-500 dark:text-slate-400">{selectedInvoice.from_company?.email}</p>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5 dark:border-white/10 dark:bg-white/5">
                  <h3 className="font-semibold text-slate-900 dark:text-white">Bill To</h3>
                  <p className="mt-2 text-slate-700 dark:text-slate-300">{selectedInvoice.to_client?.name}</p>
                  <p className="whitespace-pre-line text-sm text-slate-500 dark:text-slate-400">
                    {selectedInvoice.to_client?.address}
                  </p>
                  <p className="text-sm text-slate-500 dark:text-slate-400">{selectedInvoice.to_client?.email}</p>
                </div>
              </div>

              <div>
                <h3 className="mb-3 font-semibold text-slate-900 dark:text-white">Items</h3>
                <div className="overflow-x-auto rounded-2xl border border-slate-200 dark:border-white/10">
                  <table className="w-full">
                    <thead className="bg-slate-100 dark:bg-white/5">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm">Description</th>
                        <th className="px-4 py-3 text-right text-sm">Qty</th>
                        <th className="px-4 py-3 text-right text-sm">Rate</th>
                        <th className="px-4 py-3 text-right text-sm">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedInvoice.items?.map((item, idx) => (
                        <tr key={idx} className="border-t border-slate-200 dark:border-white/10">
                          <td className="px-4 py-3">{item.description}</td>
                          <td className="px-4 py-3 text-right">{item.qty}</td>
                          <td className="px-4 py-3 text-right">${item.rate?.toFixed(2)}</td>
                          <td className="px-4 py-3 text-right">
                            ${((item.qty || 0) * (item.rate || 0)).toFixed(2)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="space-y-1 border-t border-slate-200 pt-4 text-right dark:border-white/10">
                <p className="text-slate-600 dark:text-slate-300">Subtotal: ${selectedInvoice.subtotal?.toFixed(2)}</p>
                <p className="text-slate-600 dark:text-slate-300">
                  Tax ({selectedInvoice.taxPercent}%): ${selectedInvoice.tax?.toFixed(2)}
                </p>
                <p className="text-xl font-bold text-slate-900 dark:text-white">
                  Total: ${selectedInvoice.total?.toFixed(2)} {selectedInvoice.currency}
                </p>
              </div>

              {selectedInvoice.notes && (
                <div>
                  <h3 className="font-semibold text-slate-900 dark:text-white">Notes</h3>
                  <p className="mt-2 text-slate-600 dark:text-slate-300">{selectedInvoice.notes}</p>
                </div>
              )}

              <div className="border-t border-slate-200 pt-4 text-sm text-slate-500 dark:border-white/10 dark:text-slate-400">
                <p>Created by: {selectedInvoice.created_by}</p>
                <p>Created at: {new Date(selectedInvoice.created_at).toLocaleString()}</p>
              </div>
            </div>

            <div className="sticky bottom-0 flex justify-end gap-2 border-t border-slate-200 bg-white/95 p-5 backdrop-blur dark:border-white/10 dark:bg-slate-950/85">
              <button
                onClick={() => setShowModal(false)}
                className="rounded-2xl bg-slate-500 px-4 py-2 text-white transition hover:bg-slate-600"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
